interface text {
  text_en: string;
  text_de: string;
}

export interface User {
  id: number;
  firstName: string;
  lastName: string;
  email: string;
  password: string;
  role: roles_roleName;
  isOidcUser: boolean;
  isAdmin?: boolean;
  hasGivenConsent: boolean;
  hasSkippedGroupJoining: boolean;
}

export enum roles_roleName {
  superAdmin = 'superAdmin',
  organizationAdmin = 'organizationAdmin',
  groupAdmin = 'groupAdmin',
  researcher = 'researcher',
  user = 'user',
  anonymized = 'anonymized',
}

export interface Admin extends User {
  numberOfGroups: number;
  groups: Group[];
}

export interface UserProgress extends User {
  baselineProgress: BaselineProgress;
  dailyProgress: DailyProgress[];
}

export interface BaselineProgress {
  baselineAnswered: boolean;
  totalPossible: boolean;
}

export interface DailyProgress {
  date: Date;
  entriesCompleted: boolean;
}

export interface UserSuggestion {
  id: number;
  email: string;
  firstName?: string;
  lastName?: string;
}

export interface Group {
  id: number;
  groupName: string;
  trackingPeriodStart: Date;
  trackingPeriodFinish: Date;
  groupCode?: string;
  groupMemberships?: GroupMembership[];
}

export interface GroupMembership {
  id: number;
  userId: number;
  groupId: number;
  isAdmin: boolean;
  isFavorite: boolean;
  user: User;
  groups: Group;
}

export interface UserFlightEntry {
  id?: number;
  departureAirport: string;
  destinationAirport: string;
  cabinClass: string;
  causedEmissions: number;
}

export interface FlightFormValue {
  departureAirport: Airport | null;
  destinationAirport: Airport | null;
  cabinClass: 'economy' | 'premium_economy' | 'business' | 'first' | '';
  causedEmissions: number;
}

export interface Airport {
  iata: string;
  name: string;
  country: string;
}

export interface FlightFootprintResponse {
  footprint: number; // in kg CO2
}

export interface baselineCategory {
  id: number;
  text: text;
}

export interface baselineQuestion {
  id: string;
  text: text;
  remarksText: text;
  recommendationText: text;
  categoryId: number;
  hidden?: boolean;
  baselineAnswers: baselineAnswer[];
}

export interface baselineAnswer {
  id: number;
  factorName: string;
  text: text;
}

export interface baselineConstant {
  id: string;
  causedEmissions: number;
  text: text;
  remarksText: text;
}

export interface userBaselineEntry {
  id?: number;
  baselineQuestionId: string;
  categoryId?: number;
  baselineAnswerId: number;
  createdAt?: Date;
  updatedAt?: Date;
}

export interface Product {
  id: number;
  co2PerItem: number;
  text: text;
}

export interface userProductEntry {
  id?: number;
  createdAt?: Date;
  updatedAt?: Date;
  productId: number;
  year: number;
  causedEmissions?: number;
}

export interface foodCategory {
  id: number;
  text: text;
  foods: food[];
  icon?: string; // URL or path to the icon image
}

export interface food {
  id: number;
  categoryId: number;
  infoSource: string;
  co2PerServing: number;
  servingSize: number;
  unit: string; // e.g., 'g', 'ml', 'piece';
  text: text;
  servingText: text;
}

export interface userFoodEntry {
  id?: number;
  foodId: number;
  nrServings: number;
  importedByPlane: boolean;
  causedEmissions?: number;
  occurredAt: Date;
}

export interface mobilityVehicle {
  id: number;
  text: text;
  energySources: mobilityEnergySource[];
  icon?: string; // URL or path to the icon image
}

export interface mobilityEnergySource {
  id: number;
  text: text;
  co2PerKm: number;
  emissionId: number;
}

export interface userMobilityEntry {
  id?: number;
  mobilityEmissionId: number;
  distance: number;
  occurredAt: Date;
}

export interface activityCategory {
  id: number;
  text: text;
  activities: activity[];
  icon?: string; // URL or path to the icon image
}

export interface activity {
  id: number;
  categoryId: number;
  text: text;
  description: text;
}

export interface userActivityEntry {
  id?: number;
  activityId: number;
  duration: number;
  occurredAt: Date;
  causedEmissions: number;
}

// chart-related interfaces
export interface CategoryData {
  key: string;
  emissions: number;
  label: string;
  color: string;
}

export interface EmissionsData {
  dateString: string;
  date: Date;
  categories: CategoryData[];
  totalEmissions: number;
}

export interface ComparisonData {
  label: string;
  categories: CategoryData[];
  totalEmissions: number;
}

export interface EmissionsReport {
  userEmail: string;
  activity: EmissionsData[];
  baseline: EmissionsData[];
  constants: EmissionsData[];
  food: EmissionsData[];
  flight: EmissionsData[];
  mobility: EmissionsData[];
  product: EmissionsData[];
  userAverages: any;
  groupAverages: any;
  platformAverages: any;
}
